import React, {useRef, useState} from 'react';
import {PageHeader} from 'components/PageHeader';
import gql from 'graphql-tag';
import {TableContainer} from "shared/CommonStyles";
import {faBell} from '@fortawesome/free-solid-svg-icons';
import {
    FeedPostCategory,
    FeedPostInput,
    FeedPostMediaType,
    UpdateFeedPostInput,
    Pagination,
    useAdminFeedPostsQuery,
    useAdminMetricsQuery,
    useCreateFeedPostMutation,
    useDeleteFeedPostMutation,
    useUpdateFeedPostMutation, useSendPushAboutFeedMutation, FeedPost, useCategoriesQuery,
    FeedSortingMode,
    useFeedSortingStatusQuery,
    useUpdateFeedSortingSettingsMutation,
    useRandomizeFeedSortingMutation,
    useDisableFeedSortingMutation,
    useRunFeedSortingRotationMutation,
    FeedMediaDefaultVariant,
    useFeedMediaSettingsQuery,
    useUpdateFeedMediaSettingsMutation,
} from 'types/gqlReactTypings.generated.d';
import {GqlQueryRender} from 'shared/gql/gqlQueryRender';
import {TableWrapper} from 'shared/tableWrapper';
import {formatBoolean, formatDate, hookStateChangeInjector} from 'shared/Utilities';
import {AdminPaginator} from './components/adminPaginator';
import {Alert, Button, Col, Form, Row, Modal} from 'react-bootstrap';
import AppInput, {InputType} from 'components/AppInput';
import {FileDropzone} from '../../components/fileDropzone';
import {FetchResult} from "@apollo/client";
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faArrowDown, faArrowUp, faCompress, faCopy, faEdit, faTrash} from '@fortawesome/free-solid-svg-icons';
import {library} from '@fortawesome/fontawesome-svg-core';
import {FeedMediaOptimizationModal} from './components/feedMediaOptimizationModal';

library.add(faEdit, faTrash, faArrowDown, faArrowUp, faCompress);

interface IProps {
    match?: any;
}

interface CarouselImageEditorProps {
    value?: readonly string[] | null;
    onChange: (urls: string[]) => void;
}

const normalizeImageUrl = (uri: string) => {
    const suffixIndex = uri.search(/[?#]/);
    const path = suffixIndex >= 0 ? uri.slice(0, suffixIndex) : uri;
    const suffix = suffixIndex >= 0 ? uri.slice(suffixIndex) : '';

    return `${path.replace(/\+/g, '%2B')}${suffix}`;
};

const CarouselImageEditor: React.FC<CarouselImageEditorProps> = ({value, onChange}) => {
    const urls = value ?? [];
    const urlsRef = useRef<string[]>([...urls]);
    const [pendingUrl, setPendingUrl] = useState('');
    const [previewIndex, setPreviewIndex] = useState(0);

    React.useEffect(() => {
        urlsRef.current = [...urls];
        setPreviewIndex(current => Math.min(current, Math.max(0, urls.length - 1)));
    }, [urls]);

    const addUrl = (url: string) => {
        const normalizedUrl = normalizeImageUrl(url.trim());
        if (!normalizedUrl || urlsRef.current.includes(normalizedUrl)) {
            return;
        }
        const nextUrls = [...urlsRef.current, normalizedUrl];
        urlsRef.current = nextUrls;
        onChange(nextUrls);
        setPendingUrl('');
    };

    const move = (index: number, offset: number) => {
        const targetIndex = index + offset;
        if (targetIndex < 0 || targetIndex >= urls.length) {
            return;
        }
        const next = [...urls];
        [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
        onChange(next);
    };

    return (
        <div>
            <AppInput
                type={InputType.TEXT}
                value={pendingUrl}
                onChange={setPendingUrl}
                label='Image URL'
            />
            <Button
                type='button'
                variant='outline-primary'
                size='sm'
                style={{marginBottom: 12}}
                disabled={!pendingUrl.trim()}
                onClick={() => addUrl(pendingUrl)}
            >
                Add image URL
            </Button>
            <FileDropzone onSuccess={addUrl} accept={{'image/*': []}} maxFiles={20}/>
            {urls.length > 0 ? (
                <div style={{
                    background: '#111',
                    borderRadius: 8,
                    marginTop: 12,
                    overflow: 'hidden',
                    padding: 12,
                    textAlign: 'center',
                }}>
                    <img
                        alt={`Carousel preview ${previewIndex + 1}`}
                        src={normalizeImageUrl(urls[previewIndex])}
                        style={{height: 280, maxWidth: '100%', objectFit: 'contain'}}
                    />
                    <div style={{
                        alignItems: 'center',
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginTop: 8,
                    }}>
                        <Button
                            type='button'
                            variant='light'
                            size='sm'
                            disabled={previewIndex === 0}
                            onClick={() => setPreviewIndex(current => Math.max(0, current - 1))}
                        >
                            Previous
                        </Button>
                        <span style={{color: 'white'}}>{previewIndex + 1}/{urls.length}</span>
                        <Button
                            type='button'
                            variant='light'
                            size='sm'
                            disabled={previewIndex === urls.length - 1}
                            onClick={() => setPreviewIndex(current => Math.min(urls.length - 1, current + 1))}
                        >
                            Next
                        </Button>
                    </div>
                </div>
            ) : null}
            <div style={{marginTop: 12}}>
                {urls.map((url, index) => (
                    <div
                        key={`${url}-${index}`}
                        style={{
                            alignItems: 'center',
                            border: '1px solid #ddd',
                            borderRadius: 8,
                            display: 'flex',
                            gap: 8,
                            marginBottom: 8,
                            padding: 8,
                        }}
                    >
                        <img
                            alt={`Carousel item ${index + 1}`}
                            src={normalizeImageUrl(url)}
                            style={{height: 72, objectFit: 'cover', width: 72}}
                        />
                        <div style={{flex: 1, minWidth: 0}}>
                            <strong>{index + 1}</strong>
                            <div style={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                                {url}
                            </div>
                        </div>
                        <Button
                            type='button'
                            variant='light'
                            size='sm'
                            disabled={index === 0}
                            onClick={() => move(index, -1)}
                            aria-label={`Move image ${index + 1} up`}
                        >
                            <FontAwesomeIcon icon={faArrowUp}/>
                        </Button>
                        <Button
                            type='button'
                            variant='light'
                            size='sm'
                            disabled={index === urls.length - 1}
                            onClick={() => move(index, 1)}
                            aria-label={`Move image ${index + 1} down`}
                        >
                            <FontAwesomeIcon icon={faArrowDown}/>
                        </Button>
                        <Button
                            type='button'
                            variant='outline-danger'
                            size='sm'
                            onClick={() => onChange(urls.filter((_, itemIndex) => itemIndex !== index))}
                        >
                            Remove
                        </Button>
                    </div>
                ))}
            </div>
            <small className={urls.length > 0 && urls.length < 2 ? 'text-danger' : 'text-muted'}>
                A carousel requires at least two images. The displayed order matches this list.
            </small>
        </div>
    );
};

gql`
    query AdminFeedPosts($pagination: Pagination, $category: FeedPostCategory!) {
        adminFeedPosts(category: $category, pagination: $pagination) {
            id, userId, imageUrl, thumbnailUrl, mediaType, carouselImageUrls, category, contentSource,
            description, createdAt, archived, isPublic, categoryId, sortOrder,
            likeCount, commentCount, bookmarkCount,
            user { id, fullName }
        }
    }

    mutation CreateFeedPost($input: FeedPostInput!) {
        createFeedPost(input: $input) {
            id
        }
    }

    query FeedSortingStatus {
        feedSortingStatus {
            mode
            dailyRotationEnabled
            dailyTargetCount
            timeZone
            activePostCount
            todayPostCount
            wouldMoveCount
            lastProcessedDate
            lastRunAt
            lastRunSource
        }
    }

    mutation UpdateFeedSortingSettings($input: FeedSortingSettingsInput!) {
        updateFeedSortingSettings(input: $input) {
            mode
            dailyRotationEnabled
            dailyTargetCount
            timeZone
            activePostCount
            todayPostCount
            wouldMoveCount
            lastProcessedDate
            lastRunAt
            lastRunSource
        }
    }

    mutation RandomizeFeedSorting {
        randomizeFeedSorting {
            success
            message
            affectedCount
            addedCount
        }
    }

    mutation DisableFeedSorting {
        disableFeedSorting {
            success
            message
            affectedCount
            addedCount
        }
    }

    mutation RunFeedSortingRotation {
        runFeedSortingRotation {
            success
            message
            affectedCount
            addedCount
        }
    }

    query FeedMediaSettings {
        feedMediaSettings {
            defaultVariant
        }
    }

    mutation UpdateFeedMediaSettings($input: FeedMediaSettingsInput!) {
        updateFeedMediaSettings(input: $input) {
            defaultVariant
        }
    }
`

export const AdminFeedPosts: React.FC<IProps> = () => {
    const [pagination, setPagination] = React.useState<Pagination>();
    const query = useAdminFeedPostsQuery({
        variables: {
            pagination: {...pagination, limit: 200,},
            category: FeedPostCategory.TrendingNow
        }
    });
    const adminMetricsQuery = useAdminMetricsQuery();
    const [createFeedPostMutation] = useCreateFeedPostMutation();
    const [deleteFeedPost] = useDeleteFeedPostMutation();
    const [updateFeedPost] = useUpdateFeedPostMutation();
    const [showPushModal, setShowPushModal] = useState(false);
    const [successCreatedModal, setSuccessCreatedModal] = useState(false);
    const [pushFeedId, setPushFeedId] = useState<number>();
    const [pushDescription, setPushDescription] = useState('');
    const [sendPush] = useSendPushAboutFeedMutation();
    const {data: catData} = useCategoriesQuery();
    const feedSortingStatusQuery = useFeedSortingStatusQuery();
    const [updateFeedSortingSettings] = useUpdateFeedSortingSettingsMutation();
    const [randomizeFeedSorting] = useRandomizeFeedSortingMutation();
    const [disableFeedSorting] = useDisableFeedSortingMutation();
    const [runFeedSortingRotation] = useRunFeedSortingRotationMutation();
    const [sortingEnabled, setSortingEnabled] = useState(false);
    const [sortingTarget, setSortingTarget] = useState(10);
    const [sortingTimeZone, setSortingTimeZone] = useState('UTC');
    const [sortingBusy, setSortingBusy] = useState(false);
    const [sortingMessage, setSortingMessage] = useState<string>();
    const [showSortingModal, setShowSortingModal] = useState(false);
    const feedMediaSettingsQuery = useFeedMediaSettingsQuery();
    const [updateFeedMediaSettings] = useUpdateFeedMediaSettingsMutation();
    const [mediaVariantBusy, setMediaVariantBusy] = useState(false);

    React.useEffect(() => {
        const status = feedSortingStatusQuery.data?.feedSortingStatus;
        if (!status) return;
        setSortingEnabled(status.dailyRotationEnabled);
        setSortingTarget(status.dailyTargetCount);
        setSortingTimeZone(status.timeZone);
    }, [feedSortingStatusQuery.data]);

    const refreshSorting = async () => {
        await Promise.all([feedSortingStatusQuery.refetch(), query.refetch()]);
    };

    const handleMediaVariantChange = async (useOptimized: boolean) => {
        const defaultVariant = useOptimized ? FeedMediaDefaultVariant.Auto : FeedMediaDefaultVariant.Original;
        setMediaVariantBusy(true);
        setSortingMessage(undefined);
        try {
            await updateFeedMediaSettings({variables: {input: {defaultVariant}}});
            await feedMediaSettingsQuery.refetch();
            setSortingMessage(
                useOptimized
                    ? 'The app now serves optimized media where it is ready (original stays the fallback everywhere else).'
                    : 'The app now serves original media for every post again.'
            );
        } catch (error) {
            console.error(error);
            window.alert('Could not update the media delivery setting.');
        } finally {
            setMediaVariantBusy(false);
        }
    };

    const runSortingAction = async (action: () => Promise<any>) => {
        setSortingBusy(true);
        setSortingMessage(undefined);
        try {
            const response = await action();
            const result = response.data?.randomizeFeedSorting
                ?? response.data?.disableFeedSorting
                ?? response.data?.runFeedSortingRotation;
            if (result?.message) setSortingMessage(result.message);
            await refreshSorting();
        } catch (error) {
            console.error(error);
            window.alert('Feed sorting action failed.');
        } finally {
            setSortingBusy(false);
        }
    };

    const saveSortingSettings = async () => {
        setSortingBusy(true);
        setSortingMessage(undefined);
        try {
            await updateFeedSortingSettings({
                variables: {
                    input: {
                        dailyRotationEnabled: sortingEnabled,
                        dailyTargetCount: sortingTarget,
                        timeZone: sortingTimeZone,
                    },
                },
            });
            setSortingMessage('Feed sorting settings saved.');
            await refreshSorting();
        } catch (error) {
            console.error(error);
            window.alert('Could not save feed sorting settings.');
        } finally {
            setSortingBusy(false);
        }
    };

    const inputDefaults: FeedPostInput = {
        description: '',
        contentSource: '',
        category: FeedPostCategory.TipsAndTricks,
        categoryId: undefined,
        mediaType: FeedPostMediaType.Video,
        carouselImageUrls: [],
        isPublic: true,
    }

    const [input, setInput] = React.useState<FeedPostInput>(inputDefaults);
    const [updateInput, setUpdateInput] = useState<UpdateFeedPostInput>({description: '', categoryId: undefined});

    const change = hookStateChangeInjector(input, setInput);
    const changeUpdate = hookStateChangeInjector(updateInput, setUpdateInput);

    const [showEditModal, setShowEditModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
    const [showMediaOptimizationModal, setShowMediaOptimizationModal] = useState(false);
    const [mediaOptimizationFeedPostId, setMediaOptimizationFeedPostId] = useState<number | null>(null);

    const handleShowMediaOptimizationModal = (item: FeedPost) => {
        setMediaOptimizationFeedPostId(Number(item.id));
        setShowMediaOptimizationModal(true);
    };
    const handleCloseMediaOptimizationModal = () => {
        setShowMediaOptimizationModal(false);
        query?.refetch();
    };


    const handleShowEditModal = (item: FeedPost) => {
        setSelectedItemId(item.id.toString());
        setUpdateInput({
            description: item.description,
            categoryId: item.categoryId,
            mediaType: item.mediaType,
            carouselImageUrls: item.carouselImageUrls,
            isPublic: item.isPublic,
        });
        setShowEditModal(true);
    };

    const handleCloseEditModal = () => {
        query?.refetch();
        setSelectedItemId(null);
        setShowEditModal(false);
    };
    const handleShowCreateModal = () => {
        setInput(inputDefaults);
        setShowCreateModal(true);
    };
    const handleCloseCreateModal = () => {
        setShowCreateModal(false);
        setInput(inputDefaults);
    };

    const handleShowDeleteModal = (itemId: string) => {
        setSelectedItemId(itemId);
        setShowDeleteModal(true);
    };

    const handleCloseDeleteModal = () => {
        setSelectedItemId(null);
        setShowDeleteModal(false);
    };

    const handleDeleteItem = (id: string | null) => {
        if (id) {
            deleteFeedPost({variables: {id}}).then(() => {
                query?.refetch();
                adminMetricsQuery?.refetch();

                handleCloseDeleteModal();
            }).catch((err: Error) => {
                window.alert('Error deleting feed post');
                console.error(err);
            });
        }

        handleCloseDeleteModal();
    };

    const handleEditItem = () => {
        if (updateInput && selectedItemId) {
            if (
                updateInput.mediaType === FeedPostMediaType.ImageCarousel &&
                (updateInput.carouselImageUrls?.length ?? 0) < 2
            ) {
                window.alert('A carousel post must contain at least two images.');
                return;
            }
            updateFeedPost({
                variables: {
                    updateFeedPostId: selectedItemId,
                    input: {
                        description: updateInput.description,
                        categoryId: updateInput.categoryId,
                        mediaType: updateInput.mediaType,
                        carouselImageUrls: updateInput.carouselImageUrls,
                        isPublic: updateInput.isPublic,
                    },
                },
            }).then((response: FetchResult) => {
                console.log('After updateFeedPost:', response);
                if (response.data?.updateFeedPost) {
                    console.log('Feed post edited successfully', response.data.updateFeedPost);
                    query?.refetch();
                    handleCloseEditModal();
                }
            }).catch((error: Error) => {
                window.alert('Error editing feed post');
                console.error(error);
            });
        }
    };

    const copyIdToClipboard = (id: string) => {
        const textField = document.createElement('textarea');
        textField.innerText = id;
        document.body.appendChild(textField);
        textField.select();
        document.execCommand('copy');
        textField.remove();
        window.alert(`The ID "${id}" has been copied to the clipboard`);
    };

    const onSubmit = () => {
        if (input.mediaType === FeedPostMediaType.ImageCarousel && (input.carouselImageUrls?.length ?? 0) < 2) {
            window.alert('Add at least two images to create a carousel post.');
            return;
        }
        if (input.mediaType !== FeedPostMediaType.ImageCarousel && !input.imageUrl?.trim()) {
            window.alert('Add a video URL or upload a video.');
            return;
        }

        createFeedPostMutation({variables: {input}}).then((feed) => {
            query?.refetch();
            adminMetricsQuery?.refetch();
            console.log('FEED: ', feed);
            handleCloseCreateModal();
            // window.prompt('Feed post created. Send push to users about video?');
            setPushFeedId(feed.data?.createFeedPost?.id);
            setPushDescription(input.description);
            setSuccessCreatedModal(true)
        }).catch((err: Error) => {
            window.alert('Error creating feed post');
            console.error(err);
        })
    }

    const handleShowPushModal = (feed?: FeedPost) => {
        if (feed) {
            setPushFeedId(feed.id);
            setPushDescription(feed.description);
            setShowPushModal(true);
        }
    }

    const handleSendPush = () => {
        console.log('PUSH: ', pushFeedId, pushDescription);
        sendPush({
            variables: {
                id: pushFeedId,
                text: pushDescription,
            },
        })
            .then((response: FetchResult) => {
                console.log('Send result:', response);
                if (response.data?.sendPushAboutFeed) {
                    setShowPushModal(false);
                    setPushDescription('');
                    setPushFeedId(undefined);
                }
            })
            .catch((error: Error) => {
                window.alert('Error send push');
                console.error(error);
            });
    }


    return (
        <>
            <PageHeader title={`Feed Posts (${adminMetricsQuery.data?.adminMetrics.feedPostCount ?? '-'} total)`}
                        rightItem={
                            <>
                                <Button
                                    variant='outline-secondary'
                                    style={{marginRight: 8, whiteSpace: 'nowrap'}}
                                    onClick={() => {
                                        setSortingMessage(undefined);
                                        setShowSortingModal(true);
                                    }}
                                >
                                    Feed Settings
                                </Button>
                                <Button
                                    variant="primary"
                                    style={{whiteSpace: 'nowrap'}}
                                    onClick={handleShowCreateModal}
                                >
                                    Add Feed Post
                                </Button>
                                <AdminPaginator searchTerm='Feed Posts' pagination={pagination}
                                                onChange={setPagination}/>
                            </>
                        }/>
            <Modal
                show={showSortingModal}
                onHide={() => !sortingBusy && setShowSortingModal(false)}
                size='lg'
                centered
            >
                <Modal.Header closeButton={!sortingBusy}>
                    <Modal.Title>Feed Settings</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div style={{marginBottom: 24}}>
                        <strong>Media delivery</strong>
                        <div className='text-muted' style={{fontSize: 13, marginBottom: 8}}>
                            Controls whether the app is served the original uploaded file or the optimized
                            (smaller) version, for every post at once. Posts without a ready optimized version
                            always fall back to the original - nothing breaks either way.
                        </div>
                        {feedMediaSettingsQuery.error ? (
                            <Alert variant='danger'>Could not load media delivery settings.</Alert>
                        ) : null}
                        <Form.Check
                            type='switch'
                            id='feed-media-default-variant'
                            label={
                                feedMediaSettingsQuery.data?.feedMediaSettings.defaultVariant === FeedMediaDefaultVariant.Auto
                                    ? 'Serving optimized media where available (AUTO)'
                                    : 'Serving original media only (ORIGINAL)'
                            }
                            checked={feedMediaSettingsQuery.data?.feedMediaSettings.defaultVariant === FeedMediaDefaultVariant.Auto}
                            disabled={mediaVariantBusy || feedMediaSettingsQuery.loading}
                            onChange={event => handleMediaVariantChange(event.target.checked)}
                        />
                    </div>
                    <hr style={{margin: '0 0 24px'}}/>
                    {feedSortingStatusQuery.error ? (
                        <Alert variant='danger'>Could not load feed sorting status.</Alert>
                    ) : null}
                    {sortingMessage ? <Alert variant='success'>{sortingMessage}</Alert> : null}
                    {feedSortingStatusQuery.data?.feedSortingStatus ? (
                        <>
                            <Row style={{marginBottom: 24}}>
                                <Col xs={6} md={3} style={{marginBottom: 8}}>
                                    <div style={{background: '#f6f7f9', borderRadius: 6, height: '100%', padding: 12}}>
                                        <div className='text-muted' style={{fontSize: 13, whiteSpace: 'nowrap'}}>Mode</div>
                                        <strong style={{whiteSpace: 'nowrap'}}>
                                        {feedSortingStatusQuery.data.feedSortingStatus.mode === FeedSortingMode.Custom
                                            ? 'Custom stable order'
                                            : 'Publication date'}
                                        </strong>
                                    </div>
                                </Col>
                                <Col xs={6} md={3} style={{marginBottom: 8}}>
                                    <div style={{background: '#f6f7f9', borderRadius: 6, height: '100%', padding: 12}}>
                                        <div className='text-muted' style={{fontSize: 13, whiteSpace: 'nowrap'}}>Active posts</div>
                                        <strong>{feedSortingStatusQuery.data.feedSortingStatus.activePostCount}</strong>
                                    </div>
                                </Col>
                                <Col xs={6} md={3} style={{marginBottom: 8}}>
                                    <div style={{background: '#f6f7f9', borderRadius: 6, height: '100%', padding: 12}}>
                                        <div className='text-muted' style={{fontSize: 13, whiteSpace: 'nowrap'}}>Added today</div>
                                        <strong>{feedSortingStatusQuery.data.feedSortingStatus.todayPostCount}</strong>
                                    </div>
                                </Col>
                                <Col xs={6} md={3} style={{marginBottom: 8}}>
                                    <div style={{background: '#f6f7f9', borderRadius: 6, height: '100%', padding: 12}}>
                                        <div className='text-muted' style={{fontSize: 13, whiteSpace: 'nowrap'}}>Would move now</div>
                                        <strong>{feedSortingStatusQuery.data.feedSortingStatus.wouldMoveCount}</strong>
                                    </div>
                                </Col>
                            </Row>
                            <Form>
                                <Form.Group>
                                    <Form.Check
                                        type='switch'
                                        id='daily-feed-rotation'
                                        label='Enable daily tail rotation'
                                        checked={sortingEnabled}
                                        disabled={sortingBusy}
                                        onChange={event => setSortingEnabled(event.target.checked)}
                                    />
                                    <Form.Text className='text-muted'>
                                        If fewer posts are added during a day, move enough posts from the end to reach the daily target.
                                    </Form.Text>
                                </Form.Group>
                                <Row>
                                    <Col xs={12} md={6}>
                                    <Form.Group>
                                        <Form.Label>Daily target</Form.Label>
                                        <Form.Control
                                            type='number'
                                            min={1}
                                            max={100}
                                            value={sortingTarget}
                                            disabled={sortingBusy}
                                            onChange={event => setSortingTarget(Number(event.target.value))}
                                        />
                                    </Form.Group>
                                    </Col>
                                    <Col xs={12} md={6}>
                                    <Form.Group>
                                        <Form.Label>Time zone</Form.Label>
                                        <Form.Control
                                            value={sortingTimeZone}
                                            disabled={sortingBusy}
                                            onChange={event => setSortingTimeZone(event.target.value)}
                                            placeholder='UTC'
                                        />
                                        <Form.Text className='text-muted'>Use an IANA name, for example UTC or Europe/Kyiv.</Form.Text>
                                    </Form.Group>
                                    </Col>
                                </Row>
                            </Form>
                            <div className='text-muted' style={{marginTop: 4}}>
                                Last run: {feedSortingStatusQuery.data.feedSortingStatus.lastRunAt
                                    ? `${formatDate(feedSortingStatusQuery.data.feedSortingStatus.lastRunAt)} (${feedSortingStatusQuery.data.feedSortingStatus.lastRunSource})`
                                    : 'never'}
                                {feedSortingStatusQuery.data.feedSortingStatus.lastProcessedDate
                                    ? `; last cron date: ${feedSortingStatusQuery.data.feedSortingStatus.lastProcessedDate}`
                                    : ''}
                            </div>
                            <hr style={{margin: '24px 0 16px'}}/>
                            <strong>Order actions</strong>
                            <div className='text-muted' style={{fontSize: 13, marginBottom: 12}}>
                                Randomizing creates one stable shared order. Disabling resets all order values and returns the feed to publication date.
                            </div>
                            <div style={{display: 'flex', flexWrap: 'wrap'}}>
                                <Button
                                    variant='warning'
                                    disabled={sortingBusy}
                                    style={{marginBottom: 8, marginRight: 8, whiteSpace: 'nowrap'}}
                                    onClick={() => {
                                        if (window.confirm('Randomize all active public feed posts and enable custom stable order?')) {
                                            runSortingAction(() => randomizeFeedSorting());
                                        }
                                    }}
                                >
                                    Randomize feed
                                </Button>
                                <Button
                                    variant='outline-danger'
                                    disabled={sortingBusy}
                                    style={{marginBottom: 8, whiteSpace: 'nowrap'}}
                                    onClick={() => {
                                        if (window.confirm('Disable custom sorting and return to publication date order?')) {
                                            runSortingAction(() => disableFeedSorting());
                                        }
                                    }}
                                >
                                    Disable custom sorting
                                </Button>
                            </div>
                        </>
                    ) : null}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant='secondary'
                        disabled={sortingBusy}
                        onClick={() => setShowSortingModal(false)}
                    >
                        Close
                    </Button>
                    <Button
                        variant='outline-primary'
                        disabled={sortingBusy}
                        onClick={() => runSortingAction(() => runFeedSortingRotation())}
                    >
                        Run rotation now
                    </Button>
                    <Button disabled={sortingBusy} onClick={saveSortingSettings}>
                        Save settings
                    </Button>
                </Modal.Footer>
            </Modal>
            <TableContainer>
                <TableWrapper columns={[
                    'Preview',
                    'Order',
                    'ID',
                    'User (ID)',
                    'Media',
                    'Media URL',
                    'Category',
                    'Source',
                    'Description',
                    'Stats',
                    'Published?',
                    'Created At',
                    'Actions'
                ]}>
                    <GqlQueryRender query={query}>
                        {({adminFeedPosts}) => {
                            return (
                                <tbody>
                                {adminFeedPosts.map(item => {
                                        return (
                                            <tr key={item.id}>
                                                <td>
                                                    {item.mediaType === FeedPostMediaType.ImageCarousel ? (
                                                        <div style={{position: 'relative', width: 200}}>
                                                            <img
                                                                src={normalizeImageUrl(item.carouselImageUrls[0])}
                                                                alt={`Carousel post ${item.id}`}
                                                                style={{width: 200, height: 200, objectFit: 'cover'}}
                                                            />
                                                            <span style={{
                                                                background: 'rgba(0, 0, 0, 0.7)',
                                                                borderRadius: 12,
                                                                color: 'white',
                                                                padding: '2px 8px',
                                                                position: 'absolute',
                                                                right: 8,
                                                                top: 8,
                                                            }}>
                                                                1/{item.carouselImageUrls.length}
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <video src={item.imageUrl} style={{width: 200, height: 200}}
                                                               controls={true}/>
                                                    )}
                                                </td>
                                                <td>{item.sortOrder}</td>
                                                <td>{item.id}</td>
                                                <td>
                                                    {item.user.fullName}
                                                    <FontAwesomeIcon
                                                        icon={faCopy}
                                                        className="copy-icon"
                                                        onClick={() => copyIdToClipboard(item.user.id)}
                                                        title={item.user.id}
                                                    />
                                                </td>
                                                <td>
                                                    {item.mediaType === FeedPostMediaType.ImageCarousel
                                                        ? `Image carousel (${item.carouselImageUrls.length})`
                                                        : 'Video'}
                                                </td>
                                                <td><a href={normalizeImageUrl(item.imageUrl)} target="_blank"
                                                       rel="noopener noreferrer">LINK</a></td>
                                                <td>#{item.category}</td>
                                                <td>{item.contentSource}</td>
                                                <td>{item.description}</td>
                                                <td style={{minWidth: 150}}>
                                                    {item.likeCount} likes<br/>
                                                    {item.bookmarkCount} saves<br/>
                                                    {item.commentCount} comments<br/>
                                                </td>
                                                <td>{formatBoolean(item.isPublic)}</td>
                                                <td>{formatDate(item.createdAt)}</td>
                                                <td>
                                                    <FontAwesomeIcon
                                                        icon="edit"
                                                        className="d-block-1"
                                                        style={{marginBottom: 10}}
                                                        onClick={() => handleShowEditModal(item as FeedPost)}
                                                    />
                                                    <FontAwesomeIcon
                                                        icon={faBell}
                                                        className="d-block-1"
                                                        style={{marginBottom: 10}}
                                                        onClick={() => handleShowPushModal(item as FeedPost)}
                                                    />
                                                    <FontAwesomeIcon
                                                        icon="compress"
                                                        className="d-block-1"
                                                        style={{marginBottom: 10}}
                                                        title='Optimize media'
                                                        onClick={() => handleShowMediaOptimizationModal(item as FeedPost)}
                                                    />
                                                    <FontAwesomeIcon
                                                        icon="trash"
                                                        className="d-block-1"
                                                        style={{marginBottom: 10}}
                                                        onClick={() => handleShowDeleteModal(item.id.toString())}
                                                    />
                                                </td>
                                            </tr>
                                        )
                                    })}
                                <Modal show={showEditModal} onHide={handleCloseEditModal}>
                                    <Modal.Header closeButton>
                                        <Modal.Title>Edit feed post</Modal.Title>
                                    </Modal.Header>
                                    <Modal.Body>
                                        <AppInput
                                            type={InputType.TEXT_AREA}
                                            value={updateInput.description}
                                            onChange={changeUpdate('description')}
                                            label='Description*'
                                        />
                                        <AppInput type={InputType.SELECT} value={updateInput.categoryId}
                                                  onChange={(val) => changeUpdate('categoryId')(Number.parseInt(val.toString()))}
                                                  label='Drill Category'>
                                            <option value={undefined} key={0}>---</option>
                                            {catData?.categories.map(({id, title}) => {
                                                return <option value={id} key={id}>{title}</option>
                                            })}
                                        </AppInput>
                                        {updateInput.mediaType === FeedPostMediaType.ImageCarousel ? (
                                            <CarouselImageEditor
                                                value={updateInput.carouselImageUrls}
                                                onChange={(carouselImageUrls) => setUpdateInput(current => ({
                                                    ...current,
                                                    carouselImageUrls,
                                                }))}
                                            />
                                        ) : null}
                                        <AppInput
                                            type={InputType.SELECT}
                                            value={updateInput.isPublic === false ? 'false' : 'true'}
                                            onChange={(value) => changeUpdate('isPublic')(value === 'true')}
                                            label='Publication status'
                                        >
                                            <option value='true'>Published</option>
                                            <option value='false'>Unpublished</option>
                                        </AppInput>
                                    </Modal.Body>
                                    <Modal.Footer>
                                        <Button variant="secondary" onClick={handleCloseEditModal}>
                                            Close
                                        </Button>
                                        <Button variant="primary" onClick={handleEditItem}>
                                            Save changes
                                        </Button>
                                    </Modal.Footer>
                                </Modal>
                                <Modal show={showDeleteModal} onHide={handleCloseDeleteModal}>
                                    <Modal.Header closeButton>
                                        <Modal.Title>Delete feed post</Modal.Title>
                                    </Modal.Header>
                                    <Modal.Body>
                                        Are you sure you want to delete this feed post?
                                    </Modal.Body>
                                    <Modal.Footer>
                                        <Button variant="secondary" onClick={handleCloseDeleteModal}>
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="danger"
                                            onClick={() => {
                                                handleDeleteItem(selectedItemId);
                                            }}
                                        >
                                            Delete
                                        </Button>
                                    </Modal.Footer>
                                </Modal>
                                </tbody>
                            )
                        }}
                    </GqlQueryRender>
                </TableWrapper>
            </TableContainer>
            <Modal show={showCreateModal} onHide={handleCloseCreateModal}>
                <Modal.Header closeButton>
                    <Modal.Title>Add New Feed Post</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Row style={{marginBottom: 20}}>
                        <Col md={12}>
                            <AppInput
                                type={InputType.SELECT}
                                value={input.mediaType}
                                onChange={(value) => setInput(current => ({
                                    ...current,
                                    imageUrl: undefined,
                                    carouselImageUrls: [],
                                    mediaType: value as FeedPostMediaType,
                                }))}
                                label='Post type*'
                            >
                                <option value={FeedPostMediaType.Video}>Video</option>
                                <option value={FeedPostMediaType.ImageCarousel}>Image carousel</option>
                            </AppInput>
                            {input.mediaType === FeedPostMediaType.ImageCarousel ? (
                                <CarouselImageEditor
                                    value={input.carouselImageUrls}
                                    onChange={(carouselImageUrls) => setInput(current => ({
                                        ...current,
                                        carouselImageUrls,
                                    }))}
                                />
                            ) : (
                                <>
                                    <AppInput type={InputType.TEXT} value={input.imageUrl} onChange={change('imageUrl')}
                                              label='Video URL*'/>
                                    <FileDropzone onSuccess={file => {
                                        change('imageUrl')(file);
                                    }} accept={{'video/*': []}}/>
                                </>
                            )}
                        </Col>
                        <Col md={6}>
                            <AppInput type={InputType.SELECT} value={input.category} onChange={change('category')}
                                      label='Category*'>
                                {Object.entries(FeedPostCategory).map(([key, value]) => {
                                    if (value === FeedPostCategory.TrendingNow) {
                                        return null;
                                    }
                                    return <option value={value} key={key}>{value}</option>
                                })}
                            </AppInput>
                        </Col>
                        <Col md={6}>
                            <AppInput type={InputType.TEXT} value={input.contentSource}
                                      onChange={change('contentSource')}
                                      label='Content Source*'/>
                        </Col>
                        <Col md={12}>
                            <AppInput type={InputType.TEXT_AREA} value={input.description}
                                      onChange={change('description')}
                                      label='Description*'/>
                        </Col>
                        <Col md={12}>
                            <AppInput type={InputType.SELECT} value={input.categoryId}
                                      onChange={(val) => change('categoryId')(Number.parseInt(val.toString()))}
                                      label='Drill Category'>
                                <option value={undefined} key={0}>---</option>
                                {catData?.categories.map(({id, title}) => {
                                    return <option value={id} key={id}>{title}</option>
                                })}
                            </AppInput>
                        </Col>
                        <Col md={12}>
                            <AppInput
                                type={InputType.SELECT}
                                value={input.isPublic === false ? 'false' : 'true'}
                                onChange={(value) => change('isPublic')(value === 'true')}
                                label='Publication status'
                            >
                                <option value='true'>Published</option>
                                <option value='false'>Unpublished</option>
                            </AppInput>
                        </Col>
                    </Row>

                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseCreateModal}>Close</Button>
                    <Button variant='success' onClick={onSubmit}>Submit</Button>
                </Modal.Footer>
            </Modal>
            {/*<h4>Add New Feed Post</h4>*/}
            {/*<Row style={{marginBottom: 20}}>*/}
            {/*    <Col md={4}>*/}
            {/*        <AppInput type={InputType.TEXT} value={input.imageUrl} onChange={change('imageUrl')}*/}
            {/*                  label='Video URL*'/>*/}
            {/*        <FileDropzone onSuccess={file => {*/}
            {/*            change('imageUrl')(file);*/}
            {/*        }} accept={{'video/*': []}}/>*/}
            {/*    </Col>*/}
            {/*    <Col md={2}>*/}
            {/*        <AppInput type={InputType.SELECT} value={input.category} onChange={change('category')}*/}
            {/*                  label='Category*'>*/}
            {/*            {Object.entries(FeedPostCategory).map(([key, value]) => {*/}
            {/*                if (value === FeedPostCategory.TrendingNow) {*/}
            {/*                    return null;*/}
            {/*                }*/}
            {/*                return <option value={value} key={key}>{value}</option>*/}
            {/*            })}*/}
            {/*        </AppInput>*/}
            {/*    </Col>*/}
            {/*    <Col md={2}>*/}
            {/*        <AppInput type={InputType.TEXT} value={input.contentSource} onChange={change('contentSource')}*/}
            {/*                  label='Content Source*'/>*/}
            {/*    </Col>*/}
            {/*    <Col md={3}>*/}
            {/*        <AppInput type={InputType.TEXT} value={input.description} onChange={change('description')}*/}
            {/*                  label='Description*'/>*/}
            {/*    </Col>*/}
            {/*    <Col md={2}>*/}
            {/*        <AppInput type={InputType.SELECT} value={input.categoryId} onChange={(val) => change('categoryId')(Number.parseInt(val.toString()))}*/}
            {/*                  label='Drill Category'>*/}
            {/*            <option value={undefined} key={0}>---</option>*/}
            {/*            {catData?.categories.map(({id, title}) => {*/}
            {/*                return <option value={id} key={id}>{title}</option>*/}
            {/*            })}*/}
            {/*        </AppInput>*/}
            {/*    </Col>*/}
            {/*</Row>*/}
            {/*<Button variant='success' style={{width: '100%'}} onClick={onSubmit}>Submit</Button>*/}
            <Modal show={showPushModal} onHide={() => {
                setShowPushModal(false)
            }}>
                <Modal.Header closeButton>
                    <Modal.Title>Send push to users about video {pushFeedId}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <AppInput
                        type={InputType.TEXT_AREA}
                        value={pushDescription}
                        onChange={(val: string) => {
                            setPushDescription(val);
                        }}
                        label='Text'
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={() => {
                        setShowPushModal(false)
                    }}>
                        CLOSE
                    </Button>
                    <Button onClick={handleSendPush}>SEND</Button>
                </Modal.Footer>
            </Modal>

            <Modal show={successCreatedModal} onHide={() => {
                setSuccessCreatedModal(false)
            }}>
                <Modal.Header closeButton>
                    <Modal.Title>Feed post created. Send push to users about video?</Modal.Title>
                </Modal.Header>
                <Modal.Footer>
                    <Button onClick={() => {
                        setSuccessCreatedModal(false)
                        setPushFeedId(undefined)
                        setPushDescription('')
                    }}>
                        No
                    </Button>
                    <Button onClick={() => {
                        setSuccessCreatedModal(false)
                        setShowPushModal(true)
                    }}>
                        Yes
                    </Button>
                </Modal.Footer>
            </Modal>
            <FeedMediaOptimizationModal
                feedPostId={mediaOptimizationFeedPostId}
                show={showMediaOptimizationModal}
                onHide={handleCloseMediaOptimizationModal}
            />
        </>
    );
}
